import { useEffect, useState } from "react";
import type { OverviewModuleKey } from "../features/overview/Overview";

// Display preferences remembered in this browser. Storage can be missing or blocked, so every read
// falls back to the default rather than failing.

export type CategoryChartStyle = "donut" | "bars";
export type DailyChartStyle = "heatmap" | "line";

/** Collapsed modules are remembered separately for a phone and a wider screen. */
const overviewKey = () =>
  `barnyard-overview-v3-${window.matchMedia("(max-width: 600px)").matches ? "mobile" : "desktop"}`;

function storedCategoryChartStyle(): CategoryChartStyle {
  try {
    return localStorage.getItem("barnyard-category-chart") === "bars" ? "bars" : "donut";
  } catch {
    return "donut";
  }
}
function storedDailyChartStyle(): DailyChartStyle {
  try {
    return localStorage.getItem("barnyard-daily-chart") === "line" ? "line" : "heatmap";
  } catch {
    return "heatmap";
  }
}
function storedOverviewCollapsed(): Record<OverviewModuleKey, boolean> {
  const defaults: Record<OverviewModuleKey, boolean> = {
    monthly: false, categories: false, daily: false, rhythm: false,
    position: false, ious: false, activity: false, upcoming: false, settlement: false,
    goals: false,
  };
  try {
    const saved = JSON.parse(localStorage.getItem(overviewKey()) || "{}");
    return { ...defaults, ...saved };
  } catch {
    return defaults;
  }
}

/** The overview's chart style and collapsed modules, restored on load and saved as they change. */
export function useOverviewPreferences() {
  const [categoryChartStyle, setCategoryChartStyle] = useState<CategoryChartStyle>(storedCategoryChartStyle);
  const [dailyChartStyle, setDailyChartStyle] = useState<DailyChartStyle>(storedDailyChartStyle);
  const [overviewCollapsed, setOverviewCollapsed] = useState(storedOverviewCollapsed);
  useEffect(() => {
    try { localStorage.setItem("barnyard-category-chart", categoryChartStyle); } catch { /* Preference storage is optional. */ }
  }, [categoryChartStyle]);
  useEffect(() => {
    try { localStorage.setItem("barnyard-daily-chart", dailyChartStyle); } catch { /* Preference storage is optional. */ }
  }, [dailyChartStyle]);
  useEffect(() => {
    try { localStorage.setItem(overviewKey(), JSON.stringify(overviewCollapsed)); } catch { /* Preference storage is optional. */ }
  }, [overviewCollapsed]);
  function toggleOverviewModule(module: OverviewModuleKey) {
    setOverviewCollapsed((current) => ({ ...current, [module]: !current[module] }));
  }
  return { categoryChartStyle, setCategoryChartStyle, dailyChartStyle, setDailyChartStyle, overviewCollapsed, toggleOverviewModule };
}
